import { AbstractProjectile } from './AbstractProjectile';

/**
 * Generic pool for projectiles that efficiently reuses objects 
 * to avoid garbage collection and improve performance.
 */
export class ProjectilePool<T extends AbstractProjectile> {
  private pool: T[] = [];
  private activeProjectiles: T[] = [];
  private factory: () => T;
  private initialSize: number;
  private maxSize: number;
  private autoExpand: boolean;
  
  /**
   * Creates a new projectile pool
   * @param factory Function that creates new projectiles
   * @param initialSize Number of projectiles to pre-allocate
   * @param maxSize Maximum pool size (0 = unlimited)
   * @param autoExpand Whether to automatically create new projectiles if pool is empty
   */
  constructor(
    factory: () => T, 
    initialSize: number = 100,
    maxSize: number = 0, 
    autoExpand: boolean = true
  ) {
    this.factory = factory;
    this.initialSize = initialSize;
    this.maxSize = maxSize;
    this.autoExpand = autoExpand;
    
    // Pre-allocate objects
    this.initialize(initialSize);
  }
  
  /**
   * Initialize the pool with pre-allocated objects
   */
  private initialize(size: number): void {
    console.log(`Initializing projectile pool with ${size} objects`);
    for (let i = 0; i < size; i++) {
      try {
        const projectile = this.factory();
        projectile.deactivate();
        this.pool.push(projectile);
        if (i === 0 || i === size - 1) {
          console.log(`Created projectile ${i+1}/${size}`);
        }
      } catch (error) {
        console.error(`Error creating projectile ${i+1}/${size}:`, error);
      }
    }
    console.log(`Pool initialized with ${this.pool.length} objects`);
  }
  
  /**
   * Get a projectile from the pool
   * @returns A projectile from the pool or null if none available and autoExpand is false
   */
  public get(): T | null {
    if (this.pool.length === 0) {
      if (!this.autoExpand) {
        console.warn('Projectile pool depleted and autoExpand is disabled');
        return null;
      }
      
      // Check max size limit
      if (this.maxSize > 0 && this.activeProjectiles.length >= this.maxSize) {
        console.warn(`Cannot create more projectiles: reached maximum pool size (${this.maxSize})`);
        return null;
      }
      
      // Create a new projectile
      console.log('Projectile pool expanding automatically');
      try {
        const projectile = this.factory();
        this.activeProjectiles.push(projectile);
        console.log(`Created new projectile (active count: ${this.activeProjectiles.length})`);
        return projectile;
      } catch (error) {
        console.error('Error creating new projectile:', error);
        return null;
      }
    }
    
    // Get from pool
    const projectile = this.pool.pop()!;
    this.activeProjectiles.push(projectile);
    console.log(`Retrieved projectile from pool (pool: ${this.pool.length}, active: ${this.activeProjectiles.length})`);
    return projectile;
  }
  
  /**
   * Return a projectile to the pool
   * @param projectile The projectile to return to the pool
   */
  public release(projectile: T): void {
    // First check if it's in our active list
    const index = this.activeProjectiles.indexOf(projectile);
    if (index !== -1) {
      this.activeProjectiles.splice(index, 1);
    }
    
    // Make sure it's deactivated
    projectile.deactivate();
    
    // Add back to pool
    this.pool.push(projectile);
  }
  
  /**
   * Update all active projectiles
   * @param deltaTime Time in seconds since last update
   */
  public updateAll(deltaTime: number): void {
    // Update all active projectiles - iterate backwards for safe removal
    for (let i = this.activeProjectiles.length - 1; i >= 0; i--) {
      const projectile = this.activeProjectiles[i];
      
      // Update and check if still active
      const stillActive = projectile.update(deltaTime);
      
      // If projectile is no longer active, return it to pool
      if (!stillActive) {
        this.release(projectile);
      }
    }
  }
  
  /**
   * Get the number of active projectiles
   */
  public getActiveCount(): number {
    return this.activeProjectiles.length;
  }
  
  /**
   * Get the number of available projectiles in the pool
   */
  public getAvailableCount(): number {
    return this.pool.length;
  }
  
  /**
   * Get all active projectiles
   */
  public getActiveProjectiles(): T[] {
    return this.activeProjectiles;
  }
  
  /**
   * Release all active projectiles back to the pool
   */
  public releaseAll(): void {
    // Return all active projectiles to the pool
    while (this.activeProjectiles.length > 0) {
      const projectile = this.activeProjectiles.pop()!;
      projectile.deactivate();
      this.pool.push(projectile);
    }
  }
} 